import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [shouldCheckAdmin, setShouldCheckAdmin] = useState(false);
  const queryClient = useQueryClient();

  // Listen for token changes in localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = localStorage.getItem('token');
      if (newToken !== token) {
        setToken(newToken);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [token]);

  // Only check admin status when explicitly requested
  const { data: admin, isLoading } = useQuery(
    ['admin', token],
    async () => {
      if (!token || !shouldCheckAdmin) return null;
      
      try {
        const response = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch user profile');
        }
        
        const data = await response.json();
        const user = data.user;
        
        // Check if user is admin
        if (!user.isAdmin && !['admin', 'super_admin'].includes(user.role)) {
          // Return null instead of throwing error for non-admin users
          return null;
        }
        
        return {
          admin: {
            id: user._id,
            username: user.username,
            role: user.role,
            permissions: user.permissions,
            isAdmin: user.isAdmin
          }
        };
      } catch (error) {
        console.error('Admin profile fetch error:', error);
        // Return null instead of throwing error
        return null;
      }
    },
    {
      enabled: !!token && shouldCheckAdmin,
      retry: false,
      onError: (err) => {
        console.error('Admin auth error:', err);
        // Only logout if it's actually an authentication error, not just "not admin"
        if (err.response?.status === 401) {
          logout();
        }
      }
    }
  );

  const login = (newToken) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
    setShouldCheckAdmin(true); // Enable admin checking when logging in
    queryClient.invalidateQueries(['admin']);
    queryClient.invalidateQueries(['user']); // Also invalidate user queries for unified system
    toast.success('Admin access granted!');
  };

  const logout = () => {
    setToken(null);
    setShouldCheckAdmin(false);
    localStorage.removeItem('token');
    queryClient.clear();
    toast.success('Admin access revoked');
  };

  const checkAdminStatus = () => {
    setShouldCheckAdmin(true);
  };

  const updateAdmin = (adminData) => {
    queryClient.setQueryData(['admin', token], adminData);
  };

  const value = {
    admin: admin?.admin,
    token,
    isLoading: shouldCheckAdmin ? isLoading : false,
    isAuthenticated: !!token && !!admin,
    login,
    logout,
    checkAdminStatus,
    updateAdmin
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
